import React, { useCallback } from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ActivityImageGallery } from "../components/ActivityImages";
import {
  ActivityTypePill,
  BooleanIndicator,
  ContentSection,
  DisplayValue,
  HomeworkStatusPill,
  RecordDetailCard,
  RecordFieldRow,
  RelationshipPillList,
  SudsPill,
} from "../components/DataDisplay";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { TeamTabs } from "../components/PortalNavigation";
import { teamClientDetailPath, teamHomeworkPath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import { formatPortalDate, formatPortalDateTime } from "../utils/dateTime";
import {
  readTeamClinicId,
  withTeamClinicScope,
} from "../utils/teamClinicScope";

export function TeamHomeworkDetailPage({
  clinicianCode,
  patientId,
  homeworkAssignmentId,
}: {
  clinicianCode: string;
  patientId: string;
  homeworkAssignmentId: string;
}) {
  const clinicId = readTeamClinicId();
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getSupervisorTeamHomeworkDetail(
        clinicianCode,
        patientId,
        homeworkAssignmentId,
        signal,
      ),
    [clinicianCode, homeworkAssignmentId, patientId],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "supervisor" &&
      data.client &&
      data.clinician &&
      data.homework,
  );
  const client = available ? data?.client : null;
  const clinician = available ? data?.clinician : null;
  const homework = available ? data?.homework : null;

  return (
    <ClinicianPortalLayout
      activeGlobalKey="team"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title={homework?.homeworkName || "Team Homework Activity Detail"}
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        clinicianNameLoading={state.status === "loading"}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Home", path: "/clinician-portal" },
              {
                label: "Team",
                path: withTeamClinicScope("/clinician-portal/team", clinicId),
              },
              {
                label: "Homework Activities",
                path: withTeamClinicScope(teamHomeworkPath, clinicId),
              },
              { label: homework?.homeworkName || "Homework Activity Detail" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--profile">
        <TeamTabs
          clinicianCode={clinicianCode}
          activeKey="homework"
          clinicId={clinicId}
        />
        {state.status === "loading" ? (
          <LoadingState label="Loading team homework activity detail" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Team homework activity detail is not available")}
          />
        ) : null}
        {available && client && clinician && homework ? (
          <>
            <div className="cp-content-header">
              <div>
                <h2>Homework Activity Detail</h2>
                <p>Review this team homework assignment.</p>
              </div>
              <span className="cp-read-only-badge">Read-only</span>
            </div>
            <ContentSection title="Homework Activity Details">
              <RecordDetailCard>
                <RecordFieldRow label="Homework Activity">
                  <DisplayValue value={homework.homeworkName} />
                </RecordFieldRow>
                <RecordFieldRow label="Client">
                  <PortalLink
                    path={withTeamClinicScope(
                      teamClientDetailPath(client.id),
                      clinicId,
                    )}
                    clinicianCode={clinicianCode}
                  >
                    {client.accessCode} • {client.nickname}
                  </PortalLink>
                </RecordFieldRow>
                <RecordFieldRow label="Clinician">
                  <RelationshipPillList names={[clinician.therapistName]} />
                </RecordFieldRow>
                <RecordFieldRow label="Clinic">
                  <RelationshipPillList names={[client.clinicName]} />
                </RecordFieldRow>
                <RecordFieldRow label="Activity Type">
                  <ActivityTypePill activityType={homework.activityType} />
                </RecordFieldRow>
                <RecordFieldRow label="Value Supported">
                  <DisplayValue value={homework.valueSupportedId} />
                </RecordFieldRow>
                <RecordFieldRow label="Exposure Homework">
                  <BooleanIndicator value={!homework.nonExposureHomework} />
                </RecordFieldRow>
                <RecordFieldRow label="Activity Steps">
                  <span className="cp-multiline-value">
                    <DisplayValue value={homework.activitySteps} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Modification Steps">
                  <span className="cp-multiline-value">
                    <DisplayValue
                      value={homework.alternativeSteps || homework.alternative}
                    />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Coaching Prompts">
                  <span className="cp-multiline-value">
                    <DisplayValue value={homework.coachingPrompts} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Activity Images">
                  <ActivityImageGallery images={homework.activityImages} />
                </RecordFieldRow>
                <RecordFieldRow label="Activity Points">
                  <DisplayValue value={homework.points} />
                </RecordFieldRow>
                <RecordFieldRow label="Activity Coins">
                  <DisplayValue value={homework.coins} />
                </RecordFieldRow>
                <RecordFieldRow label="Assigned Date">
                  {formatPortalDate(homework.assignedDate)}
                </RecordFieldRow>
                <RecordFieldRow label="Due Date">
                  {formatPortalDate(homework.dueDate)}
                </RecordFieldRow>
                {homework.dueDate ? (
                  <RecordFieldRow label="Repeats">
                    <DisplayValue
                      value={
                        homework.repeatsUi ||
                        homework.repeats ||
                        "Does not repeat"
                      }
                    />
                  </RecordFieldRow>
                ) : null}
                {homework.repeats === "Weekly" ? (
                  <RecordFieldRow label="Repeat On">
                    <DisplayValue value={homework.repeatOn.join(", ")} />
                  </RecordFieldRow>
                ) : null}
                {homework.isRecurring ? (
                  <RecordFieldRow label="Recurrence End Date">
                    {formatPortalDate(homework.recurrenceEndDate)}
                  </RecordFieldRow>
                ) : null}
              </RecordDetailCard>
            </ContentSection>
            <ContentSection title="Homework Outcome">
              <RecordDetailCard>
                <RecordFieldRow label="Overdue">
                  <BooleanIndicator value={homework.overdue} />
                </RecordFieldRow>
                <RecordFieldRow label="Email Alert">
                  <BooleanIndicator value={homework.emailAlert} />
                </RecordFieldRow>
                <RecordFieldRow label="Completion Status">
                  <HomeworkStatusPill status={homework.completionStatus} />
                </RecordFieldRow>
                <RecordFieldRow label="Completed Date and Time">
                  {formatPortalDateTime(homework.completedAt)}
                </RecordFieldRow>
                <RecordFieldRow label="Summary">
                  <span className="cp-multiline-value">
                    <DisplayValue value={homework.summary} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Pre-Exposure SUDS">
                  <SudsPill
                    value={
                      homework.firstSuds === "" ? null : homework.firstSuds
                    }
                  />
                </RecordFieldRow>
                <RecordFieldRow label="Post-Exposure SUDS">
                  <SudsPill
                    value={
                      homework.secondSuds === "" ? null : homework.secondSuds
                    }
                  />
                </RecordFieldRow>
                <RecordFieldRow label="Sentiment Analysis">
                  <span className="cp-multiline-value">
                    <DisplayValue value={homework.sentimentAnalysis} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Coping Skills Used">
                  <span className="cp-multiline-value">
                    <DisplayValue value={homework.copingSkills} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Steps Altered">
                  <span className="cp-multiline-value">
                    <DisplayValue value={homework.altered} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Routine Time">
                  <DisplayValue value={homework.routineTime} />
                </RecordFieldRow>
                <RecordFieldRow label="Client Uploads">
                  <ActivityImageGallery images={homework.clientUploads} />
                </RecordFieldRow>
                <RecordFieldRow label="Conversation">
                  {homework.conversationReportUrl ? (
                    <a
                      className="cp-conversation-link"
                      href={homework.conversationReportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Log
                    </a>
                  ) : (
                    <span className="cp-empty-value">Not available</span>
                  )}
                </RecordFieldRow>
                <RecordFieldRow label="Created">
                  {formatPortalDateTime(homework.createdAt)}
                </RecordFieldRow>
              </RecordDetailCard>
            </ContentSection>
          </>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
